import React, { useState } from 'react';
import { Badge, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import type { useVoice } from '../../hooks/useVoice';

interface VoiceControlProps {
  voice: ReturnType<typeof useVoice>;
}

/**
 * Voice enable/mode/listening UI (spec 046 US5). No microphone permission is ever requested
 * until the user explicitly enables voice (FR-019); while active, a clear, always-visible
 * indicator shows listening/speaking state and a single action stops either (FR-018).
 */
const VoiceControl: React.FC<VoiceControlProps> = ({ voice }) => {
  const { supported, enabled, mode, listening, speaking, enable, disable, setMode, startTalking, stopTalking, stopSpeaking } = voice;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  if (!supported) {
    return (
      <Tooltip title="Voice isn't supported in this browser">
        <span><IconButton size="small" disabled><MicOffIcon fontSize="small" /></IconButton></span>
      </Tooltip>
    );
  }

  if (!enabled) {
    return (
      <Tooltip title="Enable voice — ask questions out loud and hear answers">
        <IconButton size="small" onClick={enable} data-testid="gn-voice-enable">
          <MicOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" data-testid="gn-voice-control">
      {(listening || speaking) && (
        <Typography variant="caption" color={listening ? 'error' : 'primary'} data-testid="gn-voice-indicator">
          {listening ? '● Listening' : '♪ Speaking'}
        </Typography>
      )}

      {mode === 'push-to-talk' ? (
        <Tooltip title="Hold to talk">
          <IconButton
            size="small"
            color={listening ? 'error' : 'default'}
            onMouseDown={startTalking}
            onMouseUp={stopTalking}
            onMouseLeave={stopTalking}
            onTouchStart={startTalking}
            onTouchEnd={stopTalking}
            data-testid="gn-voice-ptt"
          >
            <MicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title='Ambient listening for "Guruji, ..."'>
          <IconButton size="small" color={listening ? 'error' : 'default'} disabled data-testid="gn-voice-wakeword-indicator">
            <GraphicEqIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {(listening || speaking) && (
        <Tooltip title="Stop">
          <IconButton size="small" onClick={() => { stopTalking(); stopSpeaking(); }} data-testid="gn-voice-stop">
            <StopCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Voice settings">
        <Badge color="primary" variant="dot" invisible={mode === 'push-to-talk'}>
          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} data-testid="gn-voice-menu">
            <MicIcon fontSize="small" color="primary" />
          </IconButton>
        </Badge>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem selected={mode === 'push-to-talk'} onClick={() => { setMode('push-to-talk'); setMenuAnchor(null); }}>
          Hold to talk (default)
        </MenuItem>
        <MenuItem selected={mode === 'wake-word'} onClick={() => { setMode('wake-word'); setMenuAnchor(null); }}>
          Ambient — listen for "Guruji"
        </MenuItem>
        <MenuItem onClick={() => { disable(); setMenuAnchor(null); }} data-testid="gn-voice-disable">
          Turn off voice
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default VoiceControl;
